import React, { FC, useState } from 'react';
import { IconButton, Tooltip, TextField, SelectChangeEvent, Box, MenuItem, Select, Typography } from '@mui/material';
import { usePolicyStore } from 'Application/store/usePolicyStore';
import { toast } from 'sonner';
import { PolicyFlag } from 'Application/models';
import Lottie from 'react-lottie';
import CancelIcon from '@mui/icons-material/Cancel';
import AppDialog from './AppDialog';
import SuccessAnime from 'assets/lottie/waiting.json';
import { useQuery } from '@tanstack/react-query';
import { ConfigsService } from 'Application/service';
import { CanclePolicyRequest } from 'Application/models/dto/request/PolicyRequest';

interface CancelPolicyProps {
    policyNumber: string;
}

const CancelPolicy: FC<CancelPolicyProps> = ({ policyNumber }) => {
    const [open, setOpen] = useState(false);
    const { canclePolicy, isLoading } = usePolicyStore();
    const [formData, setFormData] = useState({ cancellationReasonType: '', cancellationReason: '' });

    const { data: cancellationTypes, isLoading: isGetCancellationTypesLoading } = useQuery({
        queryKey: ['getCancellationTypes'],
        queryFn: ConfigsService.getCancellationTypes,
        retry: false
    });

    const handleClickOpen = () => setOpen(true);
    const handleClose = () => {
        setOpen(false);
        setFormData({ cancellationReasonType: '', cancellationReason: '' });
    };

    const handleChangeCancellationReason = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setFormData({ ...formData, cancellationReason: event.target.value });
    };

    const handleChangeCancellationType = (event: SelectChangeEvent<string>) => {
        setFormData({ ...formData, cancellationReasonType: event.target.value });
    };

    const handleConfirm = async () => {
        if (!formData.cancellationReasonType.trim()) {
            toast.error('Please select a cancellation type');
            return;
        }

        if (!formData.cancellationReason.trim()) {
            toast.error('Please enter a cancellation reason.');
            return;
        }

        const cancelPolicyRequest: CanclePolicyRequest = {
            ...formData,
            policyNumber
        };

        try {
            const response = await canclePolicy(cancelPolicyRequest, 1, 10, PolicyFlag.ALL);

            response.error ? toast.error(response.message) : toast.success(response.message);
            handleClose();
        } catch (error) {
            console.error('Failed to cancel the policy:', error);
            toast.error(`An unexpected error occurred. Please try again. ${error}`);
            handleClose();
        }
    };

    const defaultOptions = {
        loop: true,
        autoplay: true,
        animationData: SuccessAnime,
        rendererSettings: { preserveAspectRatio: 'xMidYMid slice' }
    };

    return (
        <>
            <Tooltip placement="top" title="Cancel Policy">
                <IconButton color="error" onClick={handleClickOpen} size="large">
                    <CancelIcon sx={{ fontSize: '1.1rem', m: 1 }} />
                </IconButton>
            </Tooltip>

            <AppDialog
                Graphic={<Lottie options={defaultOptions} height={100} width={100} />}
                hasfields={true}
                isLoading={isLoading}
                loadingButtonTitle="Canceling..."
                title="Cancel Policy"
                description="By clicking Confirm, this policy will be cancelled. Please select the cancellation type and provide a reason."
                open={open}
                handleClose={handleClose}
                handleConfirm={handleConfirm}
            >
                <Box className="w-full">
                    <Typography style={{ marginBottom: '0.25rem', fontSize: '0.875rem', fontWeight: '500', color: '#718096' }}>
                        Cancellation type
                    </Typography>
                    <Select
                        fullWidth
                        variant="outlined"
                        name="cancellationReasonType"
                        value={formData.cancellationReasonType}
                        onChange={handleChangeCancellationType}
                        disabled={isGetCancellationTypesLoading}
                    >
                        {cancellationTypes?.map((type, index) => {
                            return (
                                <MenuItem key={index} value={type}>
                                    {type}
                                </MenuItem>
                            );
                        })}
                    </Select>
                </Box>
                <TextField
                    label="Cancellation Reason"
                    variant="outlined"
                    fullWidth
                    multiline
                    rows={3}
                    name="cancellationReason"
                    value={formData.cancellationReason}
                    onChange={handleChangeCancellationReason}
                />
            </AppDialog>
        </>
    );
};

export default CancelPolicy;
